import React from 'react'

import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFacetedRowModel,
  getFacetedMinMaxValues,
  getFacetedUniqueValues,
  useReactTable,
} from '@tanstack/react-table'

export default function TableWrapper({
  loadingFFData,
  loadingHubspotData,
  // Lets a caller that already renders its own, more specific loading indicator
  // (e.g. EngagementsTable's per-chunk scan progress) suppress this generic
  // fallback instead of showing both at once. Pass null to suppress; omit (or pass
  // a string) to use — default or custom — text, matching every other caller.
  loadingMessage,
  columns,
  data,
  initialSorting,
  autoResetPageIndex = true,
  renderToolbar,
  // Optional: called with the table's current columnFilters array whenever it
  // changes, so a caller that renders content outside this component (and
  // therefore can't read the table instance from renderToolbar) can still
  // react to filter changes — e.g. EngagementsTable's Shared Timestamps card.
  onFiltersChange,
}) {
  if (loadingFFData) return loadingMessage === null ? null : <div>{loadingMessage || 'Loading Fastlane Data...'}</div>
  if (loadingHubspotData) return <div>Loading Hubspot Data...</div>
  if (!data || !data.length) return <div>No data available</div>
  return (
    <Table
      columns={columns}
      data={data}
      initialSorting={initialSorting}
      autoResetPageIndex={autoResetPageIndex}
      renderToolbar={renderToolbar}
      onFiltersChange={onFiltersChange}
    />
  )
}

const calculateRowColor = (sync_status) => {
  switch(sync_status) {
    case 'hs_only':
      return 'rgba(255, 0, 0, 0.1)';
    case 'ff_only':
      return 'rgba(255, 255, 0, 0.1)';
    case 'out_of_sync':
      return '#f5f5f5';
    case 'out_of_sync_timestamp_only':
      return 'rgba(0, 123, 255, 0.05)';
    case 'ff_disabled_synced':
      return '#eeeeee';
    case 'pending_merge':
      return 'rgba(253, 126, 20, 0.15)';
    case 'merged':
      return '#e9ecef';
    case 'orphaned':
      return 'rgba(220, 53, 69, 0.15)';
    default:
      return 'white';
  }
}

function Table({ columns, data, initialSorting = [], autoResetPageIndex = true, renderToolbar, onFiltersChange }) {
  const [columnFilters, setColumnFilters] = React.useState([])

  React.useEffect(() => {
    onFiltersChange?.(columnFilters)
  }, [columnFilters, onFiltersChange])

  const table = useReactTable({
    data,
    columns,
    autoResetPageIndex,
    filterFns: {},
    state: {
      columnFilters,
    },
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(), //client side filtering
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(), // client-side faceting
    getFacetedUniqueValues: getFacetedUniqueValues(), // generate unique values for select filter/autocomplete
    getFacetedMinMaxValues: getFacetedMinMaxValues(), // generate min/max values for range filter
    isMultiSortEvent: (e) => true, // Allow multi-sorting with just click (no need for ctrl/meta key)
    debugTable: true,
    debugHeaders: true,
    debugColumns: false,
    initialState: {
      sorting: initialSorting,
      pagination: {
        pageIndex: 0, // optional, default is 0
        pageSize: 40, // custom default page size
      },
    },
  })

  return (
    <>
      <FilterBadges table={table} />
            <div className="d-flex flex-wrap align-items-center gap-2 mt-3 justify-between">
        <div style={{ marginRight: '20px '}}>
          {table.getPrePaginationRowModel().rows.length} Rows
        </div>
        <div style={{ display: 'flex', marginRight: '20px' }}>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            {'<<'}
          </button>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            {'<'}
          </button>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            {'>'}
          </button>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            {'>>'}
          </button>
        </div>
        <span className="d-flex align-items-center gap-1 ms-2" style={{ marginRight: '20px' }}>
          <span style={{ marginLeft: '5px' }}>Page</span>
          <span>
            <strong>
              {table.getState().pagination.pageIndex + 1} of{' '}
              {table.getPageCount()}
            </strong>
          </span>
        </span>

        <span className="d-flex align-items-center gap-1 ms-2" style={{ marginRight: '20px' }}>
          Go to page:
          <input
            type="number"
            min="1"
            max={table.getPageCount()}
            defaultValue={table.getState().pagination.pageIndex + 1}
            onChange={(e) => {
              const page = e.target.value ? Number(e.target.value) - 1 : 0
              table.setPageIndex(page)
            }}
            className="form-control form-control-sm"
            style={{ marginLeft: '5px', width: '80px', height: '23px', borderRadius: '4px', borderColor: 'rgb(118, 118, 118)' }}
          />
        </span>

        <select
          className="form-select form-select-sm"
          style={{ width: '120px' }}
          value={table.getState().pagination.pageSize}
          onChange={(e) => {
            table.setPageSize(Number(e.target.value))
          }}
        >
          {[40, 80, 120, 160, 200].map((pageSize) => (
            <option key={pageSize} value={pageSize}>
              Show {pageSize}
            </option>
          ))}
        </select>
        {renderToolbar ? <div style={{ marginLeft: 'auto' }}>{renderToolbar(table)}</div> : null}
      </div>
      <div className="table-responsive mt-3">
        <table className="table table-bordered table-hover mb-0 table-sm">
          <thead className="table-light">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const { width } = header.column.columnDef.meta || {}
                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className="align-middle"
                      style={width ? { width, maxWidth: width } : undefined}
                    >
                      {header.isPlaceholder ? null : (
                        <>
                          <div
                            {...{
                              className: header.column.getCanSort()
                                ? 'cursor-pointer select-none'
                                : '',
                              onClick: (event) => {
                                const column = header.column
                                if (!column.getCanSort()) return
                                // Every click is a multi-sort, so a newly clicked column would land behind
                                // any default sorts and barely change the order. Put it first instead.
                                if (column.getIsSorted()) {
                                  column.getToggleSortingHandler()(event)
                                } else {
                                  table.setSorting((prev) => [
                                    { id: column.id, desc: column.getFirstSortDir() === 'desc' },
                                    ...prev,
                                  ])
                                }
                              },
                            }}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                            {{
                              asc: ' 🔼',
                              desc: ' 🔽',
                            }[header.column.getIsSorted()] || null}
                          </div>
                          {header.column.getCanFilter() ? (
                            <div className="mt-2">
                              <Filter column={header.column} />
                            </div>
                          ) : null}
                        </>
                      )}
                    </th>
                  )
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => {
              return (
                <tr key={row.id} style={{ backgroundColor: calculateRowColor(row.original.sync_status) }}>
                  {row.getVisibleCells().map((cell) => {
                    return (
                      <td key={cell.id} className="align-middle">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <div className="d-flex flex-wrap align-items-center gap-2 mt-3 justify-between">
        <div style={{ marginRight: '20px '}}>
          {table.getPrePaginationRowModel().rows.length} Rows
        </div>
        <div style={{ display: 'flex', marginRight: '20px' }}>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            {'<<'}
          </button>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            {'<'}
          </button>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            {'>'}
          </button>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            {'>>'}
          </button>
        </div>
        <span className="d-flex align-items-center gap-1 ms-2" style={{ marginRight: '20px' }}>
          <span style={{ marginLeft: '5px' }}>Page</span>
          <span>
            <strong>
              {table.getState().pagination.pageIndex + 1} of{' '}
              {table.getPageCount()}
            </strong>
          </span>
        </span>

        <span className="d-flex align-items-center gap-1 ms-2" style={{ marginRight: '20px' }}>
          Go to page:
          <input
            type="number"
            min="1"
            max={table.getPageCount()}
            defaultValue={table.getState().pagination.pageIndex + 1}
            onChange={(e) => {
              const page = e.target.value ? Number(e.target.value) - 1 : 0
              table.setPageIndex(page)
            }}
            className="form-control form-control-sm"
            style={{ marginLeft: '5px', width: '80px', height: '23px', borderRadius: '4px', borderColor: 'rgb(118, 118, 118)' }}
          />
        </span>

        <select
          className="form-select form-select-sm"
          style={{ width: '120px' }}
          value={table.getState().pagination.pageSize}
          onChange={(e) => {
            table.setPageSize(Number(e.target.value))
          }}
        >
          {[40, 80, 120, 160, 200].map((pageSize) => (
            <option key={pageSize} value={pageSize}>
              Show {pageSize}
            </option>
          ))}
        </select>
      </div>
    </>
  )
}

function FilterBadges({ table }) {
  const columnFilters = table.getState().columnFilters;

  if (columnFilters.length === 0) return null;

  return (
    <div className="border bg-white p-3 mt-3">
      <div style={{ padding: '10px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        <strong>Active Filters:</strong>
        {columnFilters.map((filter) => {
          const column = table.getColumn(filter.id);
          const columnHeader = column.columnDef.header;

          return (
            <span
              key={filter.id}
              style={{
                border: '1px solid #b3b3b3',
                background: '#e0e0e0',
                padding: '4px 8px',
                borderRadius: '5px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '12px'
              }}
            >
              {/* Display header name instead of ID */}
              {typeof columnHeader === 'string' ? columnHeader : filter.id}: {String(filter.value)}
              <button
                onClick={() => column.setFilterValue(undefined)} // Clear filter
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  padding: 0,
                  borderRadius: '5px',
                }}
              >
                ×
              </button>
            </span>
          );
        })}
        <button className="btn btn-outline-secondary btn-sm" onClick={() => table.resetColumnFilters()}>Clear All</button>
      </div>
    </div>
  );
}

function Filter({ column }) {
  const columnFilterValue = column.getFilterValue()
  const { filterVariant, filterOptions } = column.columnDef.meta || {}

  const sortedUniqueValues = React.useMemo(
    () =>
      filterVariant === 'range'
        ? []
        : filterOptions
        ? filterOptions
        : Array.from(column.getFacetedUniqueValues().keys())
            .sort()
            .slice(0, 5000),
    [column.getFacetedUniqueValues(), filterVariant, filterOptions],
  )

  return filterVariant === 'range' ? (
    <div>
      <div className="flex space-x-2">
        {/* See faceted column filters example for min max values functionality */}
        <DebouncedInput
          style={{ width: '50%', padding: '4px', borderRadius: '4px', height: '23px' }}
          className="border"
          type="number"
          value={Array.isArray(columnFilterValue) ? (columnFilterValue[0] || '') : ''}
          onChange={(value) =>
            column.setFilterValue((old) => [value, old?.[1]])
          }
          placeholder={`Min`}
        />
        <DebouncedInput
          style={{ width: '50%', padding: '4px', borderRadius: '4px', height: '23px' }}
          className="border"
          type="number"
          value={Array.isArray(columnFilterValue) ? (columnFilterValue[1] || '') : ''}
          onChange={(value) =>
            column.setFilterValue((old) => [old?.[0], value])
          }
          placeholder={`Max`}
        />
      </div>
      <div className="h-1" />
    </div>
  ) : filterVariant === 'number' ? (
    <DebouncedInput
      style={{ width: '100%', padding: '4px', borderRadius: '4px', height: '23px' }}
      className="border"
      type="number"
      value={columnFilterValue ?? ''}
      onChange={(value) => column.setFilterValue(value)}
      placeholder="Search..."
    />
  ) : filterVariant === 'select' ? (
    <select
      onChange={(e) => column.setFilterValue(e.target.value)}
      value={columnFilterValue?.toString()}
      style={{ width: '100%', borderRadius: '4px', borderColor: 'rgb(222, 226, 230)', height: '23px', color: 'rgb(115, 119, 122)' }}
    >
      {/* See faceted column filters example for dynamic select options */}
      <option value="">All</option>
      {sortedUniqueValues.map((value) => (
        //dynamically generated select options from faceted values feature
        <option value={value} key={value}>
          {value}
        </option>
      ))}
    </select>
  ) : (
    <DebouncedInput
      className="w-36 border"
      onChange={(value) => column.setFilterValue(value)}
      placeholder={`Search...`}
      type="text"
      value={columnFilterValue || ''}
    />
    // See faceted column filters example for datalist search suggestions
  )
}

// A typical debounced input react component
function DebouncedInput({
  style,
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}) {
  const [value, setValue] = React.useState(initialValue)

  React.useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value)
    }, debounce)

    return () => clearTimeout(timeout)
  }, [value])

  return (
    <input
      {...props}
      style={style || { width: '100%', padding: '4px', borderRadius: '4px', height: '23px' }}
      value={value}
      onChange={(e) => setValue(e.target.value)}
    />
  )
}
